/**
 * dropdown that shows the actions that can be performed on a connection
 * possible actions: assign tag, break the connection, email, ask details
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbEdit, TbHeartBroken, TbMail, TbShare, TbTrash, TbUserUp } from 'react-icons/tb';
import { LiaHandshake } from 'react-icons/lia';
import WarningModal from '../modals/WarningModal';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import LocalContactModal from '../modals/LocalContactModal';
import localContactService from '../../../services/community/localContactService';
import useAdmins from '../../../hooks/community/useAdmins';
import IntroductionModal from '../modals/IntroductionModal';
import { getUserId } from '../../../services/httpService';
import InvitationLinkModal from '../modals/InvitationLinkModal';
import SharedWithCommunityModal from '../modals/SharedWithCommunityModal';
import { ActionMenu, ActionMenuButton } from '../menus/ActionMenu';
import membersService from '../../../services/community/adminService';
import { CommunityInvite } from '../../../models/inviteService.model';
import businessAdminService from '../../../services/business/adminService';
import useDashboardMember from '../../../hooks/community/useDashboardMember';

interface ConnectionActionsProps {
  dashboardMemberId: number;
  externalMemberId?: number;
  connectionType: ConnectionType;
  invite: CommunityInvite;
  handleDelete: (dashboardMemberId: number) => Promise<void>;
  communityId?: number;
  connection?: Connection;
}

export default function ConnectionActions({
  dashboardMemberId,
  externalMemberId,
  connectionType,
  invite,
  handleDelete,
  communityId,
  connection: connectionProp,
}: ConnectionActionsProps): JSX.Element {
  const { t } = useTranslation();
  const dashboardMember = useDashboardMember(dashboardMemberId);
  const connection = connectionProp || dashboardMember;
  const admins = useAdmins();
  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';

  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const [deleteAdminDialog, setDeleteAdminDialog] = React.useState(false)
  const [userIdsToDelete, setUserIdsToDelete] = React.useState<number[]>([]);
  const [localContactDialog, setLocalContactDialog] = React.useState(false);
  const [introductionDialog, setIntroductionDialog] = React.useState(false);
  const [localContactInvitationLinkDialog, setLocalContactInvitationLinkDialog] =
    React.useState(false);
  const [sharedWithCommunityDialog, setSharedWithCommunityDialog] = React.useState(false);

  const inviteContact = async (): Promise<void> => {
    if (externalMemberId) {
      await localContactService.inviteContacts([externalMemberId]);
      toast.success('Invitation sent!');
    }
  };

  const makeAdmin = async (): Promise<void> => {
    if (!connection) return;

    if (communityId !== undefined) {
      if (!dashboardMember?.memberId) return;
      await membersService.makeAdminById([connection.memberId!], communityId);
      await membersService.getAllAdmins(communityId);
    } else if (isBusiness) {
      if (!dashboardMember?.userId) return;
      await businessAdminService.makeAdminById([dashboardMember.userId]);
      await businessAdminService.getAllAdmins();
    } else {
      if (!dashboardMember?.memberId) return;
      await membersService.makeAdminById([connection.memberId!]);
      await membersService.getAllAdmins();
    }

    toast.success(t('toast.success.adminMade'));
  };

  const isAdmin = isBusiness
    ? admins.some((admin) => admin.userId === dashboardMember?.userId)
    : admins.some((admin) => admin.memberId === dashboardMember?.memberId);


  return (
    <>
      <ActionMenu>
        {connectionType === ConnectionType.MEMBER &&
          !isAdmin && (
            <ActionMenuButton
              icon={TbUserUp}
              text={t('component.actions.connection.admin')}
              onClick={() => makeAdmin()}
            />
          )}
        {connectionType === ConnectionType.MEMBER && isAdmin && dashboardMember?.userId !== +getUserId() && (
            <ActionMenuButton
              className="text-danger"
              icon={TbTrash}
              text={t('component.actions.admin.removeAdmin')}
              onClick={(): void => {
                if (isBusiness) {
                  if (dashboardMember?.userId != null) {
                    setUserIdsToDelete([dashboardMember.userId]);
                  }
                } else if (dashboardMember?.memberId != null) {
                  setUserIdsToDelete([dashboardMember.memberId]);
                }
                setDeleteAdminDialog(true)
              }}
            />
          )}
        {connectionType === ConnectionType.MEMBER && dashboardMember?.userId !== +getUserId() && (
            <ActionMenuButton
              icon={LiaHandshake}
              text={t('component.actions.connection.introduction')}
              onClick={(): void => setIntroductionDialog(true)}
            />
          )}
        {dashboardMember?.userId === +getUserId() && connectionType === ConnectionType.MEMBER && (
            <ActionMenuButton
              icon={TbShare}
              text={t('component.actions.connection.shareWithCommunity')}
              onClick={(): void => setSharedWithCommunityDialog(true)}
            />
          )}
        {connectionType === ConnectionType.EXTERNAL_MEMBER && (
          <>
            <ActionMenuButton
              icon={TbEdit}
              text={t('component.actions.connection.edit')}
              onClick={(): void => setLocalContactDialog(true)}
            />
            {dashboardMember?.fields.EMAIL && (
              <ActionMenuButton
                icon={TbMail}
                text={t('component.actions.connection.sendInvite')}
                onClick={inviteContact}
              />
            )}
            <ActionMenuButton
              icon={TbShare}
              text={t('component.actions.connection.getInvite')}
              onClick={(): void => setLocalContactInvitationLinkDialog(true)}
            />
          </>
        )}
        {(connectionType === ConnectionType.EXTERNAL_MEMBER || dashboardMember?.userId !== +getUserId()) && (
          <ActionMenuButton
            className="text-danger"
            icon={connectionType === ConnectionType.EXTERNAL_MEMBER ? TbTrash : TbHeartBroken}
            text={
              connectionType === ConnectionType.EXTERNAL_MEMBER
                ? t('component.actions.connection.deleteContact')
                : t('component.actions.connection.deleteConnection')
            }
            onClick={(): void => setDeleteDialog(true)}
          />
        )}
      </ActionMenu>
      <WarningModal
        open={deleteAdminDialog}
        setOpen={setDeleteAdminDialog}
        onContinue={async (): Promise<void> => {
          if (communityId !== undefined) {
            await membersService.deleteAdminById(userIdsToDelete, communityId);
            await membersService.getAllAdmins(communityId);
          } else if (isBusiness) {
            await businessAdminService.deleteAdmins(userIdsToDelete);
            await businessAdminService.getAllAdmins();
          } else {
            await membersService.deleteAdminById(userIdsToDelete);
            await membersService.getAllAdmins();
          }
        }}
        title={t('warning.deleteAdmin.title')}
        saveClassName="bg-danger text-secondary-50"
        description={t('warning.deleteAdmin.description')}
        button={t('warning.deleteAdmin.title')}
      />
      <WarningModal
        open={deleteDialog}
        setOpen={setDeleteDialog}
        onContinue={(): Promise<void> => handleDelete(dashboardMemberId)}
        title={
          connectionType === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.title')
            : t('warning.deleteConnection.title')
        }
        saveClassName="bg-danger text-secondary-50"
        description={
          connectionType === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.description')
            : t('warning.deleteConnection.description')
        }
        button={
          connectionType === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.title')
            : t('warning.deleteConnection.title')
        }
      />
      {connectionType === ConnectionType.EXTERNAL_MEMBER && (
        <>
          <LocalContactModal
            open={localContactDialog}
            setOpen={setLocalContactDialog}
            invite={invite}
            connection={dashboardMember}
          />
          <InvitationLinkModal
            open={localContactInvitationLinkDialog}
            setOpen={setLocalContactInvitationLinkDialog}
            externalMemberId={dashboardMember?.externalMemberId}
          />
        </>
      )}
      {connectionType === ConnectionType.MEMBER && (
        <IntroductionModal
          open={introductionDialog}
          setOpen={setIntroductionDialog}
          alias={`${dashboardMember?.fields.FIRST_NAME}+${dashboardMember?.fields.LAST_NAME}`}
          userId={dashboardMember?.memberId!}
        />
      )}
      {dashboardMember?.userId === +getUserId() && (
        <SharedWithCommunityModal
          open={sharedWithCommunityDialog}
          setOpen={setSharedWithCommunityDialog}
        />
      )}
    </>
  );
}
